import { useState, useEffect, useRef } from 'react';
import { useTimer, REST_PRESETS } from '@/contexts/TimerContext';

export default function CompactTimer() {
  const {
    isRunning,
    isFinished,
    timeLeft,
    totalTime,
    progress,
    startTimer,
    openTimer,
  } = useTimer();

  const [showPresets, setShowPresets] = useState(false);
  const waveContainerRef = useRef<HTMLDivElement>(null);
  const lastWaveTimeRef = useRef<number>(0);
  
  const isActive = isRunning || timeLeft !== totalTime || isFinished;

  // Wave animation every 3 seconds when running
  useEffect(() => {
    if (!isRunning || isFinished) return;

    const checkWave = () => {
      const now = Date.now();
      if (now - lastWaveTimeRef.current >= 3000) {
        lastWaveTimeRef.current = now;
        triggerWave();
      }
    };

    const interval = setInterval(checkWave, 100);
    return () => clearInterval(interval);
  }, [isRunning, isFinished]);

  const triggerWave = () => {
    if (!waveContainerRef.current) return;
    
    const wave = document.createElement('div');
    wave.className = 'compact-timer-wave';
    waveContainerRef.current.appendChild(wave);
    
    setTimeout(() => {
      wave.remove();
    }, 2000);
  };

  const handleClick = () => {
    if (isActive) {
      // If timer is active, open fullscreen view
      openTimer();
    } else {
      // If no timer, show presets dropdown
      setShowPresets(!showPresets);
    }
  };

  // Circle progress calculation
  const radius = 14;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (progress / 100);

  return (
    <div className="relative">
      {/* Main timer button - circular with progress */}
      <button
        onClick={handleClick}
        className="relative w-10 h-10 flex items-center justify-center transition-opacity hover:opacity-80"
      >
        {/* Wave container */}
        <div ref={waveContainerRef} className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-visible">
          {/* Waves will be added dynamically */}
        </div>

        {/* Circular progress ring */}
        <svg width="40" height="40" className="transform -rotate-90">
          {/* Background circle */}
          <circle
            cx="20"
            cy="20"
            r={radius}
            fill="none"
            stroke="rgba(0,0,0,0.1)"
            strokeWidth="2"
          />
          {/* Progress circle */}
          {isActive && (
            <circle
              cx="20"
              cy="20"
              r={radius}
              fill="none"
              stroke={isFinished ? '#22c55e' : '#0ea5e9'}
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-200"
            />
          )}
        </svg>

        {/* Center icon */}
        <div className="absolute inset-0 flex items-center justify-center">
          {isRunning ? (
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500"></span>
            </span>
          ) : isFinished ? (
            <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          ) : (
            <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
        </div>
      </button>

      {/* Presets dropdown (only when no active timer) */}
      {showPresets && !isActive && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowPresets(false)}
          />
          
          {/* Dropdown panel */}
          <div className="absolute right-0 top-full mt-2 z-50 bg-white rounded-xl shadow-xl border border-gray-200 p-3 w-52">
            <p className="text-xs text-gray-400 text-center mb-2">Время отдыха</p>
            <div className="grid grid-cols-5 gap-1.5">
              {REST_PRESETS.map((preset) => (
                <button
                  key={preset.seconds}
                  onClick={() => {
                    startTimer(preset.seconds);
                    setShowPresets(false);
                  }}
                  className="py-2 bg-gray-100 hover:bg-primary-500 hover:text-white text-gray-700 rounded-lg text-xs font-medium transition-colors"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Wave animation styles */}
      <style>{`
        .compact-timer-wave {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 40px;
          height: 40px;
          margin-left: -20px;
          margin-top: -20px;
          border-radius: 50%;
          border: 1.5px solid rgba(14, 165, 233, 0.4);
          animation: compact-wave-expand 2s ease-out forwards;
        }
        
        @keyframes compact-wave-expand {
          0% {
            transform: scale(1);
            opacity: 0.6;
          }
          100% {
            transform: scale(2.5);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}
